import React, { useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FiUpload } from "react-icons/fi";
import { FaFilePdf } from "react-icons/fa6";
import toast from "react-hot-toast";
import BreadCrumb from "../../../components/BreadCrumb";
import { useAppSelector } from "../../../redux/hooks";
import { selectSelectedLeague, selectSelectedLeagueId } from "../../../redux/slice/selectedLeagueSlice";
import { Api } from "../../../services/interceptor";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { fileToBase64, splitBase64 } from "../../../utils/fileUtils";
import { Images } from "../../../utils/imageConstants";
import { getAuthToken, getGuestModeFromStorage, resolvePublicImageUrl } from "../../../helper/helper";
import { canManageLeagueDocuments } from "../../../utils/leagueAccess";
import "./Documents.scss";

type LeagueDocument = {
  id: string;
  name: string;
  /** Resolved URL to open (CDN / absolute path from API). */
  openUrl: string;
};

const getDocumentName = (value: string) => {
  const normalized = value.split(/[\\/]/).pop() || value;
  const decoded = decodeURIComponent(normalized);
  return decoded.replace(/\.[^/.]+$/, "") || decoded;
};

const Documents: React.FC = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const token = getAuthToken();
  const isGuestModeRedux = useAppSelector((s) => s.authModal.isGuestMode);
  const isGuestMode = isGuestModeRedux || getGuestModeFromStorage();
  const canAddDocuments = Boolean(token && !isGuestMode && canManageLeagueDocuments(selectedLeague));
  const tournamentId = sessionStorage.getItem("tournamentId") ?? "";
  const teamSnapShotId = sessionStorage.getItem("teamSnapshotId") ?? "";

  const { data: fetchedDocuments = [], isLoading } = useQuery<string[]>({
    queryKey: [QUERY_KEY.getAllDocs, selectedLeagueId, tournamentId, teamSnapShotId],
    enabled: !!selectedLeagueId,
    queryFn: async () => {
      const response = await Api.get<string[]>(QUERY_KEY.getAllDocs, {
        params: {
          leagueId: selectedLeagueId,
          tournamentId,
          teamSnapShotId,
        },
      });
      return Array.isArray(response?.data) ? response.data : [];
    },
  });

  const documents = useMemo<LeagueDocument[]>(
    () =>
      fetchedDocuments.map((document, index) => {
        const raw = typeof document === "string" ? document.trim() : "";
        const openUrl = resolvePublicImageUrl(raw) || raw;
        return {
          id: `api-${index}-${raw}`,
          name: getDocumentName(raw),
          openUrl,
        };
      }),
    [fetchedDocuments]
  );

  const handleOpenDocument = (doc: LeagueDocument) => {
    const url = doc.openUrl?.trim();
    if (!url) {
      toast.error("Document link is not available.");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const uploadDocumentsMutation = useMutation({
    mutationFn: async (files: File[]) => {
      await Promise.all(
        files.map(async (file) => {
          const base64Url = await fileToBase64(file);
          const { data, type } = splitBase64(base64Url);
          const title = file.name.replace(/\.[^/.]+$/, "") || file.name;

          await Api.post(QUERY_KEY.createDocs, {
            documentType: "GENERAL",
            fileImage: {
              base64Image: data,
              fileType: type || file.type || "application/octet-stream",
            },
            fileName: file.name,
            leagueId: selectedLeagueId ?? "",
            teamSnapShotId,
            title,
            tournamentId,
          });
        })
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getAllDocs, selectedLeagueId, tournamentId, teamSnapShotId],
      });
      toast.success("Document uploaded successfully.");
    },
    onError: (error) => {
      console.error("createDocs failed:", error);
      toast.error("Failed to upload document.");
    },
  });

  const handleUploadClick = () => {
    if (!canAddDocuments) {
      toast.error("You do not have permission to add documents.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canAddDocuments) {
      toast.error("You do not have permission to add documents.");
      event.target.value = "";
      return;
    }
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    event.target.value = "";
    await uploadDocumentsMutation.mutateAsync(files);
  };

  return (
    <div className="main-middle-content-wrap">
      <div className="documents-wrapper custom-container">
        <BreadCrumb />
        <div className="documents-shell">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
            onChange={handleFileChange}
          />
          <div className="documents-header">
            <h1 className="heading">Documents</h1>
            {canAddDocuments && (
              <div className="documents-actions">
                <button
                  type="button"
                  className="documents-btn documents-btn--primary"
                  onClick={handleUploadClick}
                  disabled={uploadDocumentsMutation.isPending || isLoading}
                >
                  <FiUpload size={18} aria-hidden />
                  Add
                </button>
              </div>
            )}
          </div>

          {isLoading || uploadDocumentsMutation.isPending ? (
            <div className="documents-loading">Loading...</div>
          ) : documents.length === 0 ? (
            <div className="no-data-found">
              <img className="documents-empty-image" src={Images.no_document} alt="No documents found" />
              <h2 className="documents-empty-title">No Documents Found Yet...</h2>
              <p className="documents-empty-text">
                Upload important match or tournament documents to keep everything organized in one
                place.
              </p>
              {canAddDocuments && (
                <button
                  type="button"
                  className="documents-btn documents-btn--primary documents-empty-upload-btn"
                  onClick={handleUploadClick}
                  disabled={uploadDocumentsMutation.isPending || isLoading}
                >
                  <FiUpload size={16} aria-hidden />
                  Upload Document
                </button>
              )}
            </div>
          ) : (
            <div className="documents-grid">
              {documents.map((document) => (
                <button
                  key={document.id}
                  type="button"
                  className="document-card"
                  onClick={() => handleOpenDocument(document)}
                  title={`Open ${document.name}`}
                >
                  <div className="document-card-icon">
                    <FaFilePdf size={42} aria-hidden />
                    <span>PDF</span>
                  </div>
                  <div className="document-card-name">{document.name}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Documents;
